import { saveLogin, useLoginStatus } from "@website/hooks/auth-manager";
import { USER_TRACKING_EVENTS } from "@website/tracking/tracking.types";
import { addEventToTracking } from "@website/tracking/user";
import { isChannel } from "@website/utils/channel/helpers";
import { useCompatRouter } from "@website/widget-v2/helpers/compat-router.hook";
import {
  FORCE_LOGIN_KEY,
  validQueryStrings
} from "@website/widget/login/constants";
import omit from "lodash-es/omit";
import { useCallback } from "react";

export const useLoginModal = () => {
  const queryString = "login_step";
  const router = useCompatRouter();
  const queryValue = router?.query?.[queryString];
  const { loginStatus } = useLoginStatus();

  const onLoginModalOpen = useCallback(
    (properties?: { isWelcomeScreen?: boolean; isForceLogin?: boolean }) => {
      const { isWelcomeScreen, isForceLogin } = properties || {};
      addEventToTracking(USER_TRACKING_EVENTS.LOGIN_OPENED, {
        product: "core"
      });

      if (isChannel(["b2b", "telesale"])) {
        window.location.href = "/b2b-login";
      } else {
        router?.replace(
          router?.pathname,
          {
            ...router?.query,
            [queryString]: "phone-number",
            ...(isWelcomeScreen ? { isWelcomeScreen: "true" } : {}),
            ...(isForceLogin ? { [FORCE_LOGIN_KEY]: "true" } : {})
          },
          { shallow: true, scroll: false }
        );
      }

      return new Promise((resolve, reject) => {
        document.addEventListener("onLoginModalEnd", (...parameters) => {
          resolve(parameters);
        });
        document.addEventListener("onLoginModalClose", reject);
      });
    },
    [router]
  );

  const onLoginModalClose = useCallback(() => {
    document.dispatchEvent(
      new CustomEvent("onLoginModalClose", {
        detail: {}
      })
    );
    return router?.replace(
      router?.pathname,
      omit(router?.query, [queryString, FORCE_LOGIN_KEY]),
      { shallow: true, scroll: false }
    );
  }, [router]);

  const { isLoggedIn } = useLoginStatus();
  const onLoginModalEnd = useCallback(
    (accessToken?: string, refreshToken?: string) => {
      document.dispatchEvent(
        new CustomEvent("onLoginModalEnd", {
          detail: { accessToken, refreshToken }
        })
      );
      if (accessToken) {
        saveLogin({ accessToken, refreshToken });
      }
      onLoginModalClose();
    },
    [onLoginModalClose]
  );

  return {
    loginModalIsOpen: Boolean(
      typeof queryValue === "string" && validQueryStrings.has(queryValue)
    ),
    onLoginModalOpen,
    onLoginModalClose,
    onLoginModalEnd,
    loginModalProps: {
      open: Boolean(
        typeof queryValue === "string" && validQueryStrings.has(queryValue)
      ),
      onClose: router.query.modal ? undefined : onLoginModalClose,
      mobileHeight: "auto" as "full" | "auto",
      animate: "bottom" as "normal" | "bottom"
    },
    loginStatus,
    isLogin: isLoggedIn
  };
};
